import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import reviewService from '../../../services/reviewService';

const StarRatingInput = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);

  const labels = ['', 'Mauvais', 'Passable', 'Bien', 'Très bien', 'Excellent'];

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            className="transition-transform hover:scale-110 focus:outline-none"
            aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
          >
            <svg
              width={36}
              height={36}
              viewBox="0 0 24 24"
              fill={(hovered || value) >= star ? '#F59E0B' : 'none'}
              stroke="#F59E0B"
              strokeWidth="1.5"
              className="transition-all duration-150"
            >
              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
            </svg>
          </button>
        ))}
      </div>
      {(hovered || value) > 0 && (
        <span className="text-sm font-semibold text-amber-600 transition-all">
          {labels?.[hovered || value]}
        </span>
      )}
    </div>
  );
};

const PhotoUploadArea = ({ photos, onAdd, onRemove }) => {
  const inputRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e?.target?.files || []);
    const validFiles = files?.filter(f => f?.type?.startsWith('image/') && f?.size <= 10 * 1024 * 1024);
    if (validFiles?.length) onAdd(validFiles);
    e.target.value = '';
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3">
        {photos?.map((photo, i) => (
          <div key={i} className="relative group w-20 h-20 rounded-xl overflow-hidden border-2 border-border shadow-ios-light">
            <img
              src={URL.createObjectURL(photo)}
              alt={`Photo ${i + 1}`}
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <Icon name="X" size={16} color="white" />
            </button>
          </div>
        ))}

        {photos?.length < 5 && (
          <button
            type="button"
            onClick={() => inputRef?.current?.click()}
            className="w-20 h-20 rounded-xl border-2 border-dashed border-border hover:border-brand-earth/50 bg-surface/50 hover:bg-surface flex flex-col items-center justify-center gap-1 transition-all"
          >
            <Icon name="Plus" size={18} className="text-text-secondary/60" />
            <span className="text-xs text-text-secondary/60">Photo</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
      <p className="text-xs text-text-secondary/60">
        Max 5 photos · JPG, PNG, WebP · 10 Mo max par photo
      </p>
    </div>
  );
};

const PostBookingReviewForm = ({ booking, professional, service, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [reviewerName, setReviewerName] = useState(booking?.clientName || '');
  const [reviewerEmail, setReviewerEmail] = useState(booking?.clientEmail || '');
  const [photos, setPhotos] = useState([]);
  const [isPortfolioEligible, setIsPortfolioEligible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleAddPhotos = (newFiles) => {
    setPhotos(prev => [...prev, ...newFiles]?.slice(0, 5));
  };

  const handleRemovePhoto = (index) => {
    setPhotos(prev => prev?.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (rating === 0) {
      setError('Veuillez attribuer une note en étoiles.');
      return;
    }
    if (!reviewerName?.trim()) {
      setError('Veuillez indiquer votre nom.');
      return;
    }

    setSubmitting(true);
    setError(null);

    const { data, error: submitError } = await reviewService?.submitReview({
      bookingId: booking?.id || null,
      professionalId: professional?.id,
      serviceType: service?.id,
      rating,
      feedback: feedback?.trim() || null,
      reviewerName: reviewerName?.trim(),
      reviewerEmail: reviewerEmail?.trim() || null,
      photos,
      isPortfolioEligible,
    });

    setSubmitting(false);

    if (submitError) {
      setError('Une erreur est survenue. Veuillez réessayer.');
      return;
    }

    setSuccess(true);
    setTimeout(() => {
      onSubmitted?.(data);
    }, 2000);
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <Icon name="CheckCircle2" size={32} className="text-green-500" />
        </div>
        <h3 className="text-lg font-display font-display-semibold text-text-primary mb-2">
          Merci pour votre avis !
        </h3>
        <p className="text-sm text-text-secondary max-w-xs">
          Votre évaluation a été publiée et le profil de <strong>{professional?.name}</strong> a été mis à jour.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-border shadow-ios-medium overflow-hidden">
      {/* Header */}
      <div className={`px-6 py-5 bg-gradient-to-r ${service?.color || 'from-brand-earth to-brand-gold'} text-white`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon name="Star" size={20} color="white" />
            </div>
            <div>
              <h2 className="font-display font-display-semibold text-base">Évaluer la prestation</h2>
              <p className="text-white/80 text-xs mt-0.5">
                {professional?.name} · {service?.label}
              </p>
            </div>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors mt-0.5"
            >
              <Icon name="X" size={18} />
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Star Rating */}
        <div>
          <label className="block text-sm font-semibold text-text-primary mb-3 text-center">
            Note globale *
          </label>
          <StarRatingInput value={rating} onChange={setRating} />
        </div>

        {/* Written Feedback */}
        <div>
          <label className="block text-sm font-semibold text-text-primary mb-1.5">
            Votre avis
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e?.target?.value)}
            rows={4}
            placeholder={`Décrivez votre expérience avec ${professional?.name}. Qualité du travail, ponctualité, communication...`}
            className="w-full px-4 py-3 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all resize-none"
            maxLength={1000}
          />
          <p className="text-xs text-text-secondary/60 mt-1 text-right">{feedback?.length}/1000</p>
        </div>

        {/* Photo Upload */}
        <div>
          <label className="block text-sm font-semibold text-text-primary mb-1.5">
            Photos de la réalisation
          </label>
          <PhotoUploadArea
            photos={photos}
            onAdd={handleAddPhotos}
            onRemove={handleRemovePhoto}
          />
        </div>

        {/* Portfolio eligibility */}
        {photos?.length > 0 && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <button
              type="button"
              onClick={() => setIsPortfolioEligible(!isPortfolioEligible)}
              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                isPortfolioEligible
                  ? 'bg-brand-earth border-brand-earth' :'border-border bg-white'
              }`}
            >
              {isPortfolioEligible && <Icon name="Check" size={11} color="white" />}
            </button>
            <div>
              <p className="text-sm font-medium text-text-primary">
                Autoriser l'ajout au portfolio
              </p>
              <p className="text-xs text-text-secondary mt-0.5">
                Ces photos pourront être affichées dans le portfolio de {professional?.name} pour présenter son travail à d'autres clients.
              </p>
            </div>
          </div>
        )}

        {/* Reviewer info */}
        <div>
          <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">Vos coordonnées</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">Nom *</label>
              <input
                type="text"
                value={reviewerName}
                onChange={(e) => setReviewerName(e?.target?.value)}
                required
                placeholder="Votre nom"
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">Email</label>
              <input
                type="email"
                value={reviewerEmail}
                onChange={(e) => setReviewerEmail(e?.target?.value)}
                placeholder="votre@email.com"
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-border text-sm font-medium text-text-secondary hover:bg-surface transition-all"
            >
              Plus tard
            </button>
          )}
          <button
            type="submit"
            disabled={submitting || rating === 0}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-brand-earth text-white text-sm font-semibold hover:bg-brand-earth/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-ios-light"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Publication...
              </>
            ) : (
              <>
                <Icon name="Send" size={15} />
                Publier l'avis
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PostBookingReviewForm;
